import { OpenAI, Perplexity } from "@lobehub/icons";
import { Badge } from "@promptwatch/ui/components/badge";
import { Card, CardContent } from "@promptwatch/ui/components/card";
import { CheckIcon, ComputerIcon, FileEditIcon, MegaphoneIcon, ScanEye, ScanSearchIcon } from "lucide-react";

export default function Solution() {
  return (
    <section id="features" className="overflow-hidden py-16 lg:py-32">
      <div className="z-10 relative mx-auto mb-12 grid max-w-6xl gap-6 px-12 md:grid-cols-2 lg:gap-12">
        <h2 className="text-3xl font-semibold md:text-4xl">
          Improve AI search visibility with <span className="text-muted-foreground">strategic content optimization</span>
        </h2>

        <div className="max-w-md text-muted-foreground">
          <p>
            Uncover hidden opportunities and transform your content to capture prime visibility across AI platforms.{" "}
            <span className="font-medium text-foreground">AI-driven insights</span> power your content strategy, elevating your brand's
            authority and discoverability in the AI-first landscape.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-12">
        <div className="block px-2 sm:px-12 lg:px-2 z-10">
          <div className="relative mt-6">
            <div className="absolute -inset-20 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:24px_24px] sm:-inset-40" />
            <div className="absolute -inset-20 bg-gradient-to-b from-gray-50 via-transparent to-gray-50 sm:-inset-40" />
            <div className="absolute -inset-20 bg-gradient-to-r from-gray-50 via-transparent to-gray-50 sm:-inset-40" />
            <div className="tls-shadow-md relative overflow-hidden shadow-gray-950/[0.03]">
              <div className="relative grid gap-2 sm:grid-cols-2 lg:grid-cols-12">
                <Card className="flex flex-col justify-between gap-6 p-4 sm:col-span-full sm:p-12 lg:col-span-6">
                  <div className="relative before:absolute before:inset-x-0 before:-bottom-1 before:h-1/3 before:bg-gradient-to-b before:from-transparent before:to-background">
                    <Card className="shadow-sm">
                      <CardContent className="p-6">
                        <div className="text-lg font-medium">Content Gap Analysis</div>
                        <div className="mt-0.5 text-sm">Identify missing or underrepresented topics</div>
                        <div className="relative mb-4 mt-4 flex">
                          <div className="h-5 w-1/5 rounded-l-md bg-primary" />
                          <div className="h-5 w-1/5 bg-blue-400" />
                          <div className="h-5 w-3/5 rounded-r-md border bg-[linear-gradient(-45deg,#d4d4d8_25%,transparent_25%,transparent_50%,#d4d4d8_50%,#d4d4d8_75%,transparent_75%,transparent)] bg-[length:5px_5px]" />
                        </div>

                        <div className="mb-4 flex gap-1 border-b border-dashed pb-4">
                          <div className="w-2/5">
                            <div className="text-2xl font-medium">35%</div>
                            <div className="text-sm">Current Coverage</div>
                          </div>
                          <div className="w-3/5">
                            <div className="text-2xl font-medium">65%</div>
                            <div className="text-sm">Growth Potential</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <OpenAI.Combine className="size-6" size={24} />
                            </div>
                            <div className="mt-2 space-x-2 relative">
                              <Badge variant="secondary">Holiday season</Badge>
                              <Badge variant="secondary">Travel LA</Badge>
                            </div>
                          </div>

                          <div className="relative flex flex-col pl-4 before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-gray-200">
                            <div className="flex items-center gap-2">
                              <Perplexity.Combine type="color" className="h-6 w-fit" size={24} />
                            </div>
                            <div className="mt-2 space-y-1">
                              <Badge variant="secondary">Travel San Diego</Badge>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div>
                    <h3 className="text-base font-medium flex items-center gap-2">
                      <MegaphoneIcon className="size-4" />
                      Answer Gap Detection
                    </h3>
                    <p className="mt-4 text-sm text-muted-foreground">
                      Monitor real-time brand mentions in AI-generated answers and quickly spot gaps—where users' questions remain
                      unaddressed or incomplete—so you can create content that resonates better with both AI and potential customers.
                    </p>
                  </div>
                </Card>
                <Card className="flex flex-col justify-between gap-6 p-4 sm:col-span-full sm:p-12 lg:col-span-6">
                  <div className="relative before:absolute before:inset-x-0 before:-bottom-1 before:h-1/3 before:bg-gradient-to-b before:from-transparent before:to-background">
                    <Card className="shadow-sm">
                      <CardContent className="p-6 text-xs">
                        <div className="text-lg font-medium">Content Updates</div>

                        <div className="mt-1">
                          <div className="mt-0.5 text-sm">Update content to improve visibility</div>

                          <div className="relative mt-4 space-y-4 before:absolute before:inset-y-0 before:left-4 before:w-px before:bg-gray-200">
                            <div className="flex items-center gap-3">
                              <div className="relative flex h-8 w-8 items-center justify-center rounded-lg border bg-background shadow before:absolute before:inset-0.5 before:rounded-md before:bg-gradient-to-b before:from-gray-100">
                                <ScanSearchIcon className="h-4 w-4 text-blue-500" />
                              </div>
                              <div className="text-sm">
                                <div className="text-xs text-muted-foreground/80">Mon 24, 11:10 AM</div>
                                Analyzing Content Gaps
                              </div>
                            </div>
                            <div className="flex gap-3">
                              <div className="relative flex h-8 w-8 items-center justify-center rounded-lg border bg-background shadow before:absolute before:inset-0.5 before:rounded-md before:bg-gradient-to-b before:from-gray-100">
                                <FileEditIcon className="h-4 w-4 text-amber-500" />
                              </div>
                              <div className="mt-0.5">
                                {/* <div className="text-xs text-muted-foreground/80">
                                  Sun 30, 2025 10:00 AM
                                </div> */}
                                <div className="text-sm">Add "AI-friendly pricing page"</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="relative flex h-8 w-8 items-center justify-center rounded-lg border bg-background shadow before:absolute before:inset-0.5 before:rounded-md before:bg-gradient-to-b before:from-gray-100">
                                <ComputerIcon className="h-4 w-4 text-green-500" />
                              </div>

                              <div className="text-sm">
                                <div className="text-xs text-muted-foreground/80">Sun 30, 5:00 PM</div>
                                Scrapers indexing updated content
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="relative flex h-8 w-8 items-center justify-center rounded-lg border bg-background shadow before:absolute before:inset-0.5 before:rounded-md before:bg-gradient-to-b before:from-gray-100">
                                <CheckIcon className="h-4 w-4 text-emerald-500" />
                              </div>
                              <div className="text-sm">"Product features" page optimized</div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div>
                    <h3 className="text-base font-medium flex items-center gap-2">
                      <ScanEye className="size-4" />
                      Visibility Recommendations
                    </h3>
                    <p className="mt-4 text-sm text-muted-foreground">
                      Stay proactive with tailored suggestions to strengthen your AI-focused content. Track competitor strategies, craft new
                      pages, and optimize existing copy so you can consistently outrank rivals across AI-driven queries.
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
